<template>
    <div :class="mainClass">
        <page-header
            v-if="includeHeader"
            :title="title"
            :breadcrumbs="breadcrumbs"
            :back-route="backRoute"
        />

        <tabs
            v-if="tabsElements.length"
            :items="tabsElements"
            :active-item="activeTab"
            :actions="tabActions"
            is-card
            class="mb-3"
            @action="onTabAction"
            @item-change="activeTab = $event"
        />

        <awesome-table
            :key="tableKey"
            :labels="tableLabels"
            :items="items"
            :pagination="pagination"
            :filters="filters"
            :options="options"
            :is-loading="isLoading"
            :get-row-class="getRowClass"
            @row-click="onRowClick"
            @pagination-change="onPaginationChange"
            @add-button-click="onAddButtonClick"
            @filter-change="onFilterChange"
            @refresh="onRefresh"
            @selected-items="onSelectedItems"
        >
            <template #[`labels.actions`]="{ label }">
                <span style="color: gray">
                    {{ label }}
                </span>
            </template>

            <template #[`items.actions`]="{ item, index }">
                <button
                    v-for="(action, actionIndex) in tableActions"
                    v-show="isActionVisible(action, item)"
                    :key="`action_${actionIndex}`"
                    class="action-button"
                    @click.stop="callAction(action, item.id, index)"
                >
                    <i :class="action.icon" />
                </button>
            </template>
        </awesome-table>

        <confirmation-modal
            :content="confirmationModalContent"
            @agree="removeItem"
            @disagree="idToDelete = null"
        />

        <component
            :is="item.name"
            v-for="item of additionalComponents"
            :key="item.name"
            v-bind="item.props || {}"
            v-on="item.events || {}"
        />
    </div>
</template>

<script>
import has from 'lodash/has';
import { mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { StatusCodes as HTTP } from 'http-status-codes';

import Tabs from '@/components/elements/Tabs';
import ConfirmationModal from '@/components/elements/ConfirmationModal';

export default {
    components: {
        Tabs,
        ConfirmationModal
    },

    data() {
        let {
            page: currentPage = 1,
            perPage = 100,
            sortBy = ['createdAt:DESC'],
            q = null,
            filters = '{}'
        } = this.$route.query;

        if (!Array.isArray(sortBy)) {
            sortBy = sortBy.split(',');
        }

        return {
            mainClass: 'table-view',
            includeHeader: true,
            items: [],
            actions: [],
            selected: [],
            includeDeleteAction: true,
            customSortBy: null,
            pagination: {
                currentPage: parseInt(currentPage),
                perPage: parseInt(perPage),
                total: 0,
                search: q || '',
                sortBy
            },
            idToDelete: null,
            filters: JSON.parse(filters),
            isLoading: false,
            activeTab: {},
            tableKey: 0,
            runCreate: true,
            backRoute: '/'
        };
    },

    computed: {
        ...mapGetters({
            selectedLanguage: 'languages/selectedLanguage'
        }),

        confirmationModalContent() {
            return 'Are you sure you want to delete this item? This action cannot be undone.';
        },

        title() {
            return this.pageTitle;
        },

        breadcrumbs() {
            return [
                {
                    text: 'Cancer',
                    to: '/'
                },
                {
                    text: this.pageTitle,
                    active: true
                }
            ];
        },

        labels() {
            return [];
        },

        options() {
            return {
                filters: true
            };
        },

        tabsElements() {
            return [];
        },

        tabActions() {
            return [];
        },

        tableLabels() {
            const labels = [...this.labels];

            if (this.tableActions.length) {
                labels.push({
                    value: 'actions',
                    text: 'Actions',
                    sortable: false,
                    width: '100px',
                    filterable: false
                });
            }

            return labels;
        },

        tableActions() {
            const actions = [...this.actions];

            if (this.includeDeleteAction) {
                actions.push({
                    icon: 'far fa-trash-alt',
                    method: 'showDeleteModal'
                });
            }

            return actions;
        },

        additionalFetchData() {
            return {};
        },

        additionalComponents() {
            return [];
        },

        confirmationModalTitle() {
            return 'Are you sure you want to delete this item? This action cannot be undone.';
        }
    },

    watch: {
        selectedLanguage: {
            handler() {
                this.setAdditionalFilters();

                this.fetchItems();
            },
            deep: true
        }
    },

    created() {
        this.setAdditionalFilters();

        if (this.customSortBy) {
            this.pagination.sortBy = this.customSortBy;
        }

        if (this.runCreate) {
            this.fetchItems();
        }
    },

    methods: {
        getItems() {
            return {
                rows: [],
                count: 0
            };
        },
        deleteItem() {},
        getDeleteMessage() {
            return 'Item has been deleted.';
        },
        onRowClick() {},
        onAddButtonClick() {},
        onTabAction() {},

        setAdditionalFilters() {
            const additionalFilters = {};

            if (this.labels.some(label => label.value === 'language.code')) {
                additionalFilters['language.code'] = {
                    value: [this.selectedLanguage.code],
                    type: 'eq'
                };
            }

            this.filters = { ...this.filters, ...additionalFilters };
        },

        async fetchItems() {
            this.isLoading = true;

            try {
                const data = this.prepareFetchData();

                const { count, rows } = await this.getItems(data);

                this.items = rows;
                this.pagination.total = count;
            } catch (err) {
                console.error(err);

                this.$toasterError();
            }

            this.isLoading = false;
        },

        onSelectedItems(items) {
            this.selected = items;

            this.$emit('selected-items', items);
        },

        onPaginationChange(pagination) {
            this.pagination = cloneDeep(pagination);

            this.fetchItems();
        },

        onFilterChange(filters) {
            this.filters = { ...filters };

            this.fetchItems();
        },

        showDeleteModal(id) {
            this.idToDelete = id;
            this.$bvModal.show('confirmModal');
        },

        callAction(action, id, index) {
            this[action.method](id, index);
        },

        setupDeleteData(idToDelete) {
            return idToDelete;
        },

        async removeItem() {
            try {
                const deleteData = this.setupDeleteData(this.idToDelete);

                await this.deleteItem(deleteData);

                this.items = this.items.filter(
                    item => item.id !== this.idToDelete
                );
                this.idToDelete = null;
                this.pagination.total--;

                this.$toaster(this.getDeleteMessage());
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        isActionVisible(action, item) {
            const { where } = action;

            if (!where) {
                return true;
            }

            for (const key in where) {
                const condition = where[key];

                if (
                    (condition.negative && item[key]) ||
                    (!condition.negative && !item[key])
                ) {
                    return false;
                }
            }

            return true;
        },

        getRowClass() {
            return '';
        },

        onRefresh() {
            this.fetchItems();
        },

        prepareFetchData() {
            const fetchData = {};
            const additionalFetchData = cloneDeep(this.additionalFetchData);

            if (has(additionalFetchData, 'filters')) {
                const { filters } = additionalFetchData;

                delete additionalFetchData['filters'];

                fetchData.filters = {
                    ...this.filters,
                    ...filters
                };
            } else {
                fetchData.filters = this.filters;
            }

            return {
                ...fetchData,
                ...this.pagination,
                ...additionalFetchData
            };
        }
    }
};
</script>
